import type { AiOperationTargetRef } from '../../../types/aiOperational'
import type { ChecklistItem, TimelineBoardKey } from '../../../types/schema'
import type { TimelineElement } from '../../../types/timelineElement'

export interface TimelineRuleIssue {
  code:
    | 'TASK_OUTSIDE_GOAL'
    | 'SUBTASK_OUTSIDE_TASK'
    | 'CHECKLIST_OUTSIDE_PARENT'
    | 'CHECKLIST_ORPHAN'
    | 'DEPENDENCY_BLOCKED'
    | 'COMPLETION_GATE'
  severity: 'INFO' | 'WARN' | 'BLOCKER'
  target: AiOperationTargetRef
  message: string
}

export interface TimelineRuleContext {
  summary: string
  issues: TimelineRuleIssue[]
  selectedTargets: AiOperationTargetRef[]
  pulseHints: Array<{ id: string; summary: string; severity?: string; targetId?: string }>
}

const isoTime = (value: string | null | undefined) => {
  if (!value) return null
  const time = new Date(value).getTime()
  return Number.isFinite(time) ? time : null
}

const withinRange = (
  start: string | null | undefined,
  end: string | null | undefined,
  parentStart: string | null | undefined,
  parentEnd: string | null | undefined,
) => {
  const childStart = isoTime(start)
  const childEnd = isoTime(end)
  const ancestorStart = isoTime(parentStart)
  const ancestorEnd = isoTime(parentEnd)
  if (childStart === null || childEnd === null || ancestorStart === null || ancestorEnd === null) return true
  return childStart >= ancestorStart && childEnd <= ancestorEnd
}

const elementTarget = (element: TimelineElement): AiOperationTargetRef => ({
  id: element.id,
  type: element.type,
  title: element.title,
  board: element.board,
  parentId: element.parentId,
})

const checklistTarget = (item: ChecklistItem, parent?: TimelineElement): AiOperationTargetRef => ({
  id: item.id,
  type: 'CHECKLIST',
  title: item.title,
  board: item.board ?? parent?.board,
  parentId: item.parentContainerId,
})

const targetKey = (target: AiOperationTargetRef) => `${target.type}:${target.id}`

const selectedElementIds = (selectedTargets: AiOperationTargetRef[]) =>
  new Set(selectedTargets.flatMap((target) => [target.id, target.parentId].filter((id): id is string => Boolean(id))))

const statusLooksBlocked = (item: ChecklistItem) =>
  Boolean(item.blockedByExternal || item.blockedByExternalId || (item.prerequisites ?? []).length > 0 || (item.dependencyNodeIds ?? []).length > 0)

export const buildTimelineRuleContext = (input: {
  elements: TimelineElement[]
  checklistItems: ChecklistItem[]
  selectedTargets?: AiOperationTargetRef[]
  board?: TimelineBoardKey
}): TimelineRuleContext => {
  const elementsById = new Map(input.elements.map((element) => [element.id, element]))
  const selectedTargets = input.selectedTargets ?? []
  const selectedIds = selectedElementIds(selectedTargets)
  const issues: TimelineRuleIssue[] = []

  input.elements.forEach((element) => {
    const parent = element.parentId ? elementsById.get(element.parentId) : undefined
    if (!parent) return
    if (element.type === 'TASK' && parent.type === 'GOAL' && !withinRange(element.startDate, element.endDate, parent.startDate, parent.endDate)) {
      issues.push({
        code: 'TASK_OUTSIDE_GOAL',
        severity: 'BLOCKER',
        target: elementTarget(element),
        message: `Task "${element.title}" has dates outside goal "${parent.title}".`,
      })
    }
    if (element.type === 'SUBTASK' && parent.type === 'TASK' && !withinRange(element.startDate, element.endDate, parent.startDate, parent.endDate)) {
      issues.push({
        code: 'SUBTASK_OUTSIDE_TASK',
        severity: 'BLOCKER',
        target: elementTarget(element),
        message: `Subtask "${element.title}" has dates outside task "${parent.title}".`,
      })
    }
  })

  input.checklistItems.forEach((item) => {
    const parent = elementsById.get(item.parentContainerId)
    const target = checklistTarget(item, parent)
    if (!parent) {
      issues.push({
        code: 'CHECKLIST_ORPHAN',
        severity: 'BLOCKER',
        target,
        message: `Checklist "${item.title}" is missing its owning timeline node.`,
      })
      return
    }
    const start = item.scheduleStartDate ?? item.startDate
    const end = item.scheduleEndDate ?? item.endDate ?? start
    if (!withinRange(start, end, parent.startDate, parent.endDate)) {
      issues.push({
        code: 'CHECKLIST_OUTSIDE_PARENT',
        severity: 'BLOCKER',
        target,
        message: `Checklist "${item.title}" is scheduled outside parent "${parent.title}".`,
      })
    }
    if (statusLooksBlocked(item)) {
      issues.push({
        code: 'DEPENDENCY_BLOCKED',
        severity: selectedIds.has(item.id) || selectedIds.has(item.parentContainerId) ? 'BLOCKER' : 'WARN',
        target,
        message: `Checklist "${item.title}" has dependency blockers that must be respected before completion/status changes.`,
      })
    }
    if (item.requiresProofFile || item.requiresLocationPin || item.requiresCoSign) {
      issues.push({
        code: 'COMPLETION_GATE',
        severity: selectedIds.has(item.id) || selectedIds.has(item.parentContainerId) ? 'BLOCKER' : 'INFO',
        target,
        message: `Checklist "${item.title}" has completion gates: ${[
          item.requiresProofFile ? 'proof file' : '',
          item.requiresLocationPin ? 'location pin' : '',
          item.requiresCoSign ? 'co-sign' : '',
        ].filter(Boolean).join(', ')}.`,
      })
    }
  })

  const relevantIssues = selectedTargets.length
    ? issues.filter((issue) => selectedIds.has(issue.target.id) || (issue.target.parentId ? selectedIds.has(issue.target.parentId) : false))
    : issues.filter((issue) => issue.severity !== 'INFO').slice(0, 8)
  const uniqueSelected = selectedTargets.filter((target, index, all) =>
    all.findIndex((candidate) => targetKey(candidate) === targetKey(target)) === index)
  const pulseHints = relevantIssues.slice(0, 6).map((issue) => ({
    id: `rule-${issue.code}-${issue.target.id}`,
    targetId: issue.target.id,
    severity: issue.severity,
    summary: issue.message,
  }))
  const counts = {
    goals: input.elements.filter((element) => element.type === 'GOAL').length,
    tasks: input.elements.filter((element) => element.type === 'TASK').length,
    subtasks: input.elements.filter((element) => element.type === 'SUBTASK').length,
    checklists: input.checklistItems.length,
    blockers: relevantIssues.filter((issue) => issue.severity === 'BLOCKER').length,
  }

  return {
    selectedTargets: uniqueSelected,
    issues: relevantIssues,
    pulseHints,
    summary: [
      `Workspace graph: ${counts.goals} goals, ${counts.tasks} tasks, ${counts.subtasks} subtasks, ${counts.checklists} checklist nodes.`,
      input.board ? `Active board: ${input.board}.` : '',
      counts.blockers ? `${counts.blockers} relevant blocker(s) found in timeline/checklist constraints.` : 'No relevant blocker found in retrieved timeline/checklist constraints.',
    ].filter(Boolean).join(' '),
  }
}
